import { useState, useCallback, useRef, useEffect } from 'react';
import { apiGet } from './apiClient';
import { indexerTradeToMarketTrade } from './sidebarTradeTypes';
import type { IndexerTradeItem } from './sidebarTradeTypes';
import type { MarketTrade } from './useMarketTrades';

interface TradesResponse {
    event_id: string;
    market_ids: string[];
    trades: IndexerTradeItem[];
    trade_count: number;
    has_more: boolean;
}

const PAGE_SIZE = 200;

/** A newer request aborted this one while its response was still in flight. */
function wasSuperseded(controller: AbortController): boolean {
    return controller.signal.aborted;
}

/** The cursor for the next page: the oldest row's timestamp, or null if empty. */
function nextCursor(page: IndexerTradeItem[]): string | null {
    return page.length > 0 ? page[page.length - 1].timestamp : null;
}

/**
 * True when the next request would repeat this one, so paging cannot progress.
 * Happens for real: the cursor is a timestamp, and one market can trade a full
 * page inside a single one.
 */
function cursorStalled(
    cursor: string | null,
    sentCursor: string | null
): boolean {
    return cursor === null || cursor === sentCursor;
}

/** Rows inside [from, to]; returns the original array when nothing is dropped. */
function withinWindow(
    trades: MarketTrade[],
    fromDate?: string | null,
    toDate?: string | null
): MarketTrade[] {
    const fromMs = fromDate ? new Date(fromDate).getTime() : -Infinity;
    const toMs = toDate ? new Date(toDate).getTime() : Infinity;
    const kept = trades.filter(t => {
        const ts = t.timestamp.getTime();
        return ts >= fromMs && ts <= toMs;
    });
    return kept.length === trades.length ? trades : kept;
}

/** Newest-first, deduped by id. */
function appendNewTrades(
    prev: MarketTrade[],
    incoming: MarketTrade[]
): MarketTrade[] {
    const existingIds = new Set(prev.map(t => t.id));
    const added = incoming.filter(t => !existingIds.has(t.id));
    if (added.length === 0) return prev;
    return [...prev, ...added].sort(
        (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
    );
}

export function useEventTrades(
    eventId: string | null,
    fromDate?: string | null,
    toDate?: string | null
) {
    const [trades, setTrades] = useState<MarketTrade[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [loadedForWindow, setLoadedForWindow] = useState(0);
    const cursorRef = useRef<string | null>(null);
    const loadingRef = useRef(false);
    const hasMoreRef = useRef(true);
    const abortRef = useRef<AbortController | null>(null);

    const loadPage = useCallback(async () => {
        if (!eventId || loadingRef.current || !hasMoreRef.current) return;

        loadingRef.current = true;
        setLoading(true);
        setError(null);
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        try {
            const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
            if (fromDate) params.set('from_ts', fromDate);
            const sentCursor = cursorRef.current;
            if (sentCursor) params.set('to_ts', sentCursor);
            else if (toDate) params.set('to_ts', toDate);

            const res = await apiGet<TradesResponse>(
                `/api/trades/event/${encodeURIComponent(eventId)}?${params}`,
                controller.signal
            );
            if (wasSuperseded(controller)) return;

            const mapped = res.trades.map(indexerTradeToMarketTrade);
            const cursor = nextCursor(res.trades);
            const stalled = cursorStalled(cursor, sentCursor);

            if (stalled && res.has_more) {
                console.warn(
                    `[useEventTrades] cursor stalled at ${sentCursor} for event ${eventId} — stopping pagination`
                );
            }

            if (cursor !== null) cursorRef.current = cursor;

            const more = res.has_more && !stalled;
            hasMoreRef.current = more;
            setHasMore(more);

            setLoadedForWindow(n => n + mapped.length);

            if (mapped.length > 0) {
                setTrades(prev => appendNewTrades(prev, mapped));
            }
        } catch (err) {
            if (err instanceof Error && err.name === 'AbortError') return;
            console.error(`Failed to load trades for event ${eventId}:`, err);
            setError(err instanceof Error ? err : new Error(String(err)));
        } finally {
            if (!controller.signal.aborted) {
                loadingRef.current = false;
                setLoading(false);
            }
        }
    }, [eventId, fromDate, toDate]);

    const reset = useCallback(() => {
        abortRef.current?.abort();
        setTrades([]);
        hasMoreRef.current = true;
        setHasMore(true);
        setError(null);
        setLoadedForWindow(0);
        cursorRef.current = null;
        loadingRef.current = false;
        setLoading(false);
    }, []);

    // Auto-reset when eventId changes
    const prevEventIdRef = useRef(eventId);
    useEffect(() => {
        if (prevEventIdRef.current !== eventId) {
            prevEventIdRef.current = eventId;
            reset();
        }
    }, [eventId, reset]);

    /**
     * Restart paging for a new window, keeping the rows that still belong to it
     * so zooming the chart doesn't blank the list while the rescoped fetch
     * refills behind it. Unlike an event change this prunes rather than clears.
     */
    const resetForWindow = useCallback(() => {
        abortRef.current?.abort();
        cursorRef.current = null;
        hasMoreRef.current = true;
        loadingRef.current = false;
        setHasMore(true);
        setError(null);
        setLoading(false);
        setLoadedForWindow(0);
        setTrades(prev => withinWindow(prev, fromDate, toDate));
    }, [fromDate, toDate]);

    const rangeKey = `${fromDate ?? ''}|${toDate ?? ''}`;
    const prevRangeKeyRef = useRef(rangeKey);
    useEffect(() => {
        if (prevRangeKeyRef.current === rangeKey) return;
        prevRangeKeyRef.current = rangeKey;
        resetForWindow();
    }, [rangeKey, resetForWindow]);

    // Abort any in-flight request on unmount
    useEffect(() => () => abortRef.current?.abort(), []);

    return {
        trades,
        loading,
        hasMore,
        error,
        loadedForWindow,
        loadPage,
        reset,
    };
}
